import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapPin, Clock, Calendar, ShieldCheck, ArrowRight, Bus, Wifi, Snowflake, BatteryCharging, Luggage, MessageCircle, Banknote, Car, Upload } from "lucide-react";
import { toast } from "sonner";
import { IMG, PRIVATE_IMG } from "@/lib/assets";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";

export default function Landing() {
  const { t } = useLang();
  const [prices, setPrices] = useState(null);
  useEffect(() => {
    api.get("/config").then((r) => setPrices(r.data.prices)).catch(() => {});
  }, []);
  const p = prices || { eur: { one_way: 35, round_trip: 65 }, usd: { one_way: 40, round_trip: 75 } };
  return (
    <div className="bg-[#F5F3F0]">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1C1917]">
        <img
          src={IMG.aeropuerto}
          alt="Aeropuerto Internacional José Martí"
          width="1600" height="1000"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-2 gap-10 items-center">
          <div className="text-white fade-up">
            <div className="text-xs tracking-[0.3em] uppercase text-white/70 mb-4">
              {t("hero.kicker")}
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-lg">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/book"
                data-testid="cta-hero-reservar"
                className="bg-[#8C4A32] hover:bg-[#6F3A27] text-white px-7 py-4 rounded-full inline-flex items-center gap-2 transition-colors"
              >
                {t("hero.ctaColectivo")} <ArrowRight size={18} />
              </Link>
              <Link
                to="/private"
                data-testid="cta-hero-private"
                className="bg-white/10 backdrop-blur border border-white/40 hover:bg-white/20 text-white px-7 py-4 rounded-full inline-flex items-center gap-2 transition-colors"
              >
                {t("hero.ctaPrivada")}
              </Link>
              <a
                href="#ruta"
                data-testid="cta-hero-ruta"
                className="text-white/80 hover:text-white px-3 py-4 inline-flex items-center gap-2 transition-colors"
              >
                {t("hero.verRuta")}
              </a>
            </div>
          </div>

          <div className="fade-up hidden md:block">
            <div className="bg-white rounded-2xl shadow-2xl p-6 border border-[#DCD7D0]">
              <div className="text-xs tracking-[0.2em] uppercase text-stone-500">
                {t("hero.tarifas")}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-[#F5F3F0] border border-[#DCD7D0]">
                  <div className="text-sm text-stone-500">{t("hero.soloIda")}</div>
                  <div className="font-serif text-4xl text-stone-900 mt-1">{p.eur.one_way}€</div>
                  <div className="text-xs text-stone-500 mt-1">{t("hero.orPrefix")} {p.usd.one_way} USD</div>
                </div>
                <div className="p-5 rounded-xl bg-[#8C4A32] text-white">
                  <div className="text-sm opacity-80">{t("hero.idaVuelta")}</div>
                  <div className="font-serif text-4xl mt-1">{p.eur.round_trip}€</div>
                  <div className="text-xs mt-1 opacity-80">{t("hero.orPrefix")} {p.usd.round_trip} USD</div>
                </div>
              </div>
              <div className="mt-5 text-sm text-stone-600 flex items-center gap-2">
                <ShieldCheck size={16} className="text-[#3A5A40]" />
                {t("hero.cancelFree")}
              </div>
              <div className="mt-2 text-xs text-stone-500 flex items-center gap-2">
                <Wifi size={14} />
                {t("hero.methods")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RUTA */}
      <section id="ruta" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("ruta.kicker")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">
              {t("ruta.title")}
            </h2>
            <p className="text-stone-700 mt-5 leading-relaxed">
              {t("ruta.desc")}
            </p>
            <ul className="mt-8 space-y-5">
              <li className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#8C4A32] text-white flex items-center justify-center shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="font-medium">{t("ruta.salida")}</div>
                  <div className="text-sm text-stone-600">{t("ruta.salidaSub")}</div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-white border border-[#8C4A32] text-[#8C4A32] flex items-center justify-center shrink-0">
                  <Bus size={18} />
                </div>
                <div>
                  <div className="font-medium">{t("ruta.recogida")}</div>
                  <div className="text-sm text-stone-600">{t("ruta.recogidaSub")}</div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#3A5A40] text-white flex items-center justify-center shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="font-medium">{t("ruta.llegada")}</div>
                  <div className="text-sm text-stone-600">{t("ruta.llegadaSub")}</div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-white border border-stone-300 text-stone-700 flex items-center justify-center shrink-0">
                  <Clock size={18} />
                </div>
                <div>
                  <div className="font-medium">{t("ruta.regreso")}</div>
                  <div className="text-sm text-stone-600">{t("ruta.regresoSub")}</div>
                </div>
              </li>
            </ul>
          </div>
          <div className="relative">
            <img
              src={IMG.minibus}
              alt="Nuestro microbús"
              width="1024" height="683"
              loading="lazy"
              className="rounded-2xl shadow-lg w-full aspect-[3/2] object-cover bg-[#1C1917]"
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl border border-[#DCD7D0] p-5 shadow-md hidden md:block">
              <div className="text-xs tracking-[0.2em] uppercase text-stone-500">{t("ruta.asientos")}</div>
              <div className="font-serif text-2xl text-stone-900 mt-1">{t("ruta.filas")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* QUIÉNES SOMOS */}
      <section id="nosotros" className="bg-[#1C1917] text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-white/60">{t("about.kicker")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">{t("about.title")}</h2>
            <p className="mt-6 text-white/80 leading-relaxed">{t("about.desc")}</p>
            <ul className="mt-8 space-y-3">
              {[t("about.b1"), t("about.b2"), t("about.b3")].map((b) => (
                <li key={b} className="flex items-center gap-3 text-white/90">
                  <ShieldCheck size={18} className="text-[#D9C3A0] shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-5">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-white/60 text-xs tracking-[0.2em] uppercase">
                <MapPin size={15} /> {t("about.sedesTitle")}
              </div>
              <div className="mt-3 font-serif text-2xl">{t("about.sedes")}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-white/60 text-xs tracking-[0.2em] uppercase">
                <Bus size={15} /> {t("about.destinosTitle")}
              </div>
              <p className="mt-3 text-white/85 leading-relaxed">{t("about.destinos")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* DESTINOS */}
      <section className="bg-[#EBE8E3] border-y border-[#DCD7D0]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("dest.kicker")}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">
            {t("dest.title")}
          </h2>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-4">
            <DestCard img={IMG.parque} title="Parque Serafín Sánchez" caption={t("dest.parque")} />
            <DestCard img={IMG.cabaiguan} title="Paseo de Cabaiguán" caption={t("dest.cabaiguan")} />
            <DestCard img={IMG.embajada} title="Embajada (Consulado)" caption={t("dest.embajada")} />
            <DestCard img={IMG.lonja} title="La Lonja del Comercio" caption={t("dest.lonja")} />
            <DestCard img={IMG.aeropuerto} title="Aeropuerto José Martí" caption={t("dest.aeropuerto")} />
          </div>
        </div>
      </section>

      {/* CONFORT + EQUIPAJE */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("confort.kicker")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">
              {t("confort.title")}
            </h2>
            <ul className="mt-8 space-y-5">
              <li className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#8C4A32] text-white flex items-center justify-center shrink-0">
                  <Snowflake size={18} />
                </div>
                <div>
                  <div className="font-medium">{t("confort.ac")}</div>
                  <div className="text-sm text-stone-600">{t("confort.acSub")}</div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#8C4A32] text-white flex items-center justify-center shrink-0">
                  <BatteryCharging size={18} />
                </div>
                <div>
                  <div className="font-medium">{t("confort.carga")}</div>
                  <div className="text-sm text-stone-600">{t("confort.cargaSub")}</div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#3A5A40] text-white flex items-center justify-center shrink-0">
                  <ShieldCheck size={18} />
                </div>
                <div>
                  <div className="font-medium">{t("confort.choferes")}</div>
                  <div className="text-sm text-stone-600">{t("confort.choferesSub")}</div>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("equipaje.kicker")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">
              {t("equipaje.title")}
            </h2>
            <p className="text-stone-700 mt-5 leading-relaxed">
              {t("equipaje.desc")}
            </p>
            <div className="mt-6 flex items-start gap-4 p-5 bg-white border border-[#DCD7D0] rounded-2xl">
              <div className="w-10 h-10 rounded-full bg-[#8C4A32] text-white flex items-center justify-center shrink-0">
                <Luggage size={18} />
              </div>
              <div className="text-sm text-stone-700">
                <div className="font-medium text-stone-900">{t("equipaje.boxTitle")}</div>
                <div className="mt-1">
                  {t("equipaje.boxDesc")}{" "}
                  <a
                    href="https://wa.me/34611841896"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="wa-contact-link"
                    className="text-[#8C4A32] font-medium hover:underline inline-flex items-center gap-1"
                  >
                    <MessageCircle size={14} /> 611 841 896
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OTROS DESTINOS DISPONIBLES (PRIVADOS) */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("priv.kicker")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">
              {t("priv.title")}
            </h2>
            <p className="text-stone-700 mt-3 max-w-2xl">
              {t("priv.desc")}
            </p>
          </div>
          <Link
            to="/private"
            data-testid="cta-private-destinos"
            className="bg-[#8C4A32] hover:bg-[#6F3A27] text-white text-sm font-medium px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors"
          >
            {t("priv.cta")} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <DestCard img={PRIVATE_IMG.vinales} title="Viñales" caption="Valle · Pinar del Río" />
          <DestCard img={PRIVATE_IMG.varadero} title="Varadero" caption="Playa · Matanzas" />
          <DestCard img={PRIVATE_IMG.matanzas} title="Matanzas" caption="La Atenas de Cuba" />
          <DestCard img={PRIVATE_IMG.cienfuegos} title="Cienfuegos" caption="La Perla del Sur" />
          <DestCard img={PRIVATE_IMG.santaClara} title="Santa Clara" caption="Ciudad · Aeropuerto" />
          <DestCard img={PRIVATE_IMG.cayoSantaMaria} title="Cayo Santa María" caption="Playa paradisíaca" />
          <DestCard img={PRIVATE_IMG.cayoCoco} title="Cayo Coco / Guillermo" caption="Jardines del Rey" />
          <DestCard img={PRIVATE_IMG.ciegoDeAvila} title="Ciego de Ávila" caption="Ciudad · Centro de Cuba" />
          <DestCard img={PRIVATE_IMG.camaguey} title="Camagüey" caption="Patrimonio UNESCO" />
        </div>
      </section>

      {/* HORARIO */}
      <section id="horario" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("horario.kicker")}</div>
        <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">{t("horario.title")}</h2>
        <div className="mt-4 inline-flex items-center gap-2 bg-[#3A5A40]/10 text-[#3A5A40] rounded-full px-4 py-2 text-sm font-medium" data-testid="horario-flexible">
          <Clock size={15} /> {t("horario.flexible")}
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[t("horario.mon"), t("horario.wed"), t("horario.fri")].map((d) => (
            <div key={d} className="bg-white rounded-2xl p-8 border border-[#DCD7D0]">
              <Calendar className="text-[#8C4A32] mb-4" />
              <div className="font-serif text-3xl text-stone-900">{d}</div>
              <div className="mt-2 text-stone-600 text-sm">{t("horario.times")}</div>
              <div className="mt-6 h-px bg-[#DCD7D0]" />
              <div className="mt-4 text-sm text-stone-500">{t("horario.reserva")}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MÉTODOS DE PAGO */}
      <section id="pagos" className="bg-[#EBE8E3] border-y border-[#DCD7D0]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("pagos.kicker")}</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">
                {t("pagos.title")}
              </h2>
              <ul className="mt-8 space-y-4 text-stone-700">
                <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-[#8C4A32] mt-2 shrink-0" /> {t("pagos.card")}</li>
                <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-[#8C4A32] mt-2 shrink-0" /> {t("pagos.bizum")}</li>
                <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-[#8C4A32] mt-2 shrink-0" /> {t("pagos.transfer")}</li>
                <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-[#8C4A32] mt-2 shrink-0" /> {t("pagos.cash")}</li>
              </ul>
            </div>
            <div className="bg-white border-2 border-dashed border-[#D97706] rounded-2xl p-6">
              <div className="flex items-center gap-2 text-[#D97706] font-medium">
                <Banknote size={18} /> {t("pagos.cashTitle")}
              </div>
              <p className="text-stone-700 text-sm mt-3">
                {t("pagos.cashDesc")}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Cabaiguán","Sancti Spíritus","Cienfuegos","Matanzas","La Habana","Tenerife","Gran Canaria"].map((c) => (
                  <span key={c} className="text-xs bg-[#F5F3F0] border border-[#DCD7D0] rounded-full px-3 py-1.5 text-stone-800">
                    {c}
                  </span>
                ))}
              </div>
              <p className="text-sm text-stone-700 mt-4">
                <MessageCircle size={13} className="inline mr-1 text-[#3A5A40]" />
                {t("pagos.waHint")}
              </p>
              <div className="mt-4 pt-4 border-t border-[#DCD7D0]">
                <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-2">{t("pagos.monedas")}</div>
                <div className="flex flex-wrap gap-2">
                  {["EUR €","USD $","CUP"].map((c) => (
                    <span key={c} className="text-xs font-medium bg-[#3A5A40]/10 text-[#3A5A40] rounded-full px-3 py-1">{c}</span>
                  ))}
                </div>
              </div>
              <p className="text-xs text-stone-500 mt-4">
                {t("pagos.liberada")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POLÍTICAS */}
      <section id="politicas" className="bg-white border-t border-[#DCD7D0]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-xs tracking-[0.3em] uppercase text-stone-500">{t("pol.kicker")}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3 text-stone-900">{t("pol.title")}</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <PolicyCard tone="success" title={t("pol.p1t")} desc={t("pol.p1d")} />
            <PolicyCard tone="warning" title={t("pol.p2t")} desc={t("pol.p2d")} />
            <PolicyCard tone="error" title={t("pol.p3t")} desc={t("pol.p3d")} />
          </div>
        </div>
      </section>

      {/* TRABAJA CON NOSOTROS / CHOFERES */}
      <section id="choferes" className="bg-[#3A5A40] text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-white/60">{t("driver.kicker")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">{t("driver.title")}</h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-md">{t("driver.desc")}</p>
            <div className="mt-8 flex items-center gap-3 text-white/70 text-sm">
              <Bus size={18} /> {t("about.sedes")}
            </div>
          </div>
          <DriverRecruitment t={t} />
        </div>
      </section>

      <footer className="border-t border-[#DCD7D0] bg-[#F5F3F0]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between gap-4 text-sm text-stone-600">
          <div>© {new Date().getFullYear()} RutaEspirituana · Sancti Spíritus ↔ La Habana</div>
          <Link to="/admin/login" className="hover:text-[#8C4A32]" data-testid="footer-admin-link">
            {t("footer.admin")}
          </Link>
        </div>
      </footer>
    </div>
  );
}

function DestCard({ img, title, caption }) {
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-[#DCD7D0]">
      <div className="aspect-[4/3] overflow-hidden bg-[#1C1917]">
        <img
          src={img}
          alt={title}
          width="600" height="450"
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className="font-serif text-lg text-stone-900">{title}</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mt-1">{caption}</div>
      </div>
    </div>
  );
}

function PolicyCard({ tone, title, desc }) {
  const toneMap = {
    success: "border-[#3A5A40] bg-[#3A5A40]/5",
    warning: "border-[#D97706] bg-[#D97706]/5",
    error: "border-[#991B1B] bg-[#991B1B]/5",
  };
  return (
    <div className={`rounded-2xl p-8 border-2 ${toneMap[tone]}`}>
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-800">{title}</div>
      <div className="font-serif text-2xl mt-3 text-stone-900">{desc}</div>
    </div>
  );
}

function DriverRecruitment({ t }) {
  const [form, setForm] = useState({ full_name: "", phone: "", location: "", car_type: "", routes: "" });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.location.trim() || !form.car_type.trim()) {
      toast.error(t("driver.required")); return;
    }
    if (photo && photo.size > 6 * 1024 * 1024) { toast.error("Máx. 6 MB"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name.trim());
      fd.append("phone", form.phone.trim());
      fd.append("location", form.location.trim());
      fd.append("car_type", form.car_type.trim());
      fd.append("routes", form.routes.trim());
      if (photo) fd.append("photo", photo);
      await api.post("/driver-application", fd);
      toast.success(t("driver.success"));
      setForm({ full_name: "", phone: "", location: "", car_type: "", routes: "" });
      setPhoto(null);
    } catch {
      toast.error(t("driver.error"));
    } finally { setSending(false); }
  };

  const inputCls = "w-full bg-[#F5F3F0] border border-[#DCD7D0] focus:border-[#3A5A40] outline-none rounded-lg px-3 py-2.5 text-sm";

  return (
    <form onSubmit={submit} data-testid="driver-form" className="bg-white rounded-2xl p-6 md:p-7 text-stone-800 shadow-2xl">
      <div className="flex items-center gap-2 text-[#3A5A40] font-medium mb-4">
        <Car size={18} /> {t("driver.kicker")}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <DField label={t("driver.name")}>
          <input data-testid="driver-name" value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Nombre y apellidos" />
        </DField>
        <DField label={t("driver.phone")}>
          <input data-testid="driver-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+53 …" />
        </DField>
        <DField label={t("driver.location")}>
          <input data-testid="driver-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="Sancti Spíritus, Habana…" />
        </DField>
        <DField label={t("driver.carType")}>
          <input data-testid="driver-cartype" value={form.car_type} onChange={set("car_type")} className={inputCls} placeholder="4 o 10 plazas, marca…" />
        </DField>
      </div>
      <div className="mt-4">
        <DField label={t("driver.routes")}>
          <textarea data-testid="driver-routes" value={form.routes} onChange={set("routes")} rows={2} className={inputCls} placeholder="Ej. SSP → Habana 150€ · Habana → Varadero 90€" />
        </DField>
      </div>
      <div className="mt-4">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-2">{t("driver.photo")}</div>
        <label className="flex items-center gap-3 bg-[#F5F3F0] border border-dashed border-[#DCD7D0] rounded-lg px-3 py-3 cursor-pointer hover:border-[#3A5A40]">
          <Upload size={16} className="text-stone-500" />
          <span className="text-sm text-stone-600 truncate">{photo ? photo.name : t("driver.photoHint")}</span>
          <input data-testid="driver-photo" type="file" accept="image/*" className="hidden"
            onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
        </label>
      </div>
      <button data-testid="driver-submit" disabled={sending}
        className="mt-5 w-full bg-[#3A5A40] hover:bg-[#2a4230] disabled:opacity-50 text-white py-3 rounded-full inline-flex items-center justify-center gap-2 transition-colors">
        {sending ? t("driver.sending") : t("driver.submit")} <ArrowRight size={16} />
      </button>
    </form>
  );
}

function DField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-2">{label}</div>
      {children}
    </label>
  );
}
